"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Droplets, ShieldCheck, SlidersHorizontal, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  title: "Aplicação sem névoa",
  description: "Distribuição uniforme de fluidos e óleos que mantém o ambiente de trabalho limpo.",
  icon: Droplets
}, {
  title: "Dosagem micrométrica",
  description: "Controlo de caudal ajustável que elimina o excesso de consumo e desperdício.",
  icon: SlidersHorizontal
}, {
  title: "Fiabilidade comprovada",
  description: "Componentes certificados Spraying Systems Co. para operação industrial contínua.",
  icon: ShieldCheck
}];
const highlights = ["Representante exclusivo Spraying Systems Co.", "Sistemas integrados de controlo AutoJet", "Suporte técnico especializado em Portugal"];
export default function Lubrificacao() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="herolubrificacao" data-nav="dark" className="relative w-full overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      {/* Background Decorative Gradients */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 size-96 rounded-full bg-secondary/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 size-96 rounded-full bg-accent-foreground/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-secondary/15 via-primary to-primary opacity-80" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          {/* Left Column: Copy & Actions */}
          <motion.div {...animationProps} className="lg:col-span-7 flex flex-col items-start text-left">
            <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground">
              <Sparkles className="size-3.5 mr-1" />
              Sistemas de Pulverização e Aplicação
            </Badge>

            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-primary-foreground leading-tight">Lubrificação&nbsp;</h1>

            <p className="mt-5 text-base sm:text-lg md:text-xl text-primary-foreground/80 max-w-2xl leading-relaxed">O processo de lubrificação no fabrico industrial exige a aplicação controlada de uma película fluida fina — como um lubrificante, agente de desmoldagem ou inibidor de corrosão — para reduzir o atrito, prevenir a aderência, proteger superfícies e melhorar o manuseamento de materiais. Quando a película é aplicada de forma consistente e no alvo, as operações registam menos defeitos, maior vida útil das ferramentas, equipamentos mais limpos e menor consumo de fluidos.</p>

            {/* Quick highlights checklist */}
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-y-2.5 gap-x-6 text-sm text-primary-foreground/90">
              {highlights.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-secondary shrink-0" />
                  <span>{item}</span>
                </div>)}
            </div>

            {/* Action CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md font-semibold px-6 py-2.5">
                <Link to="/ContactsPage" className="inline-flex items-center gap-2">
                  <span>Consulte-nos</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/20 bg-primary/40 text-primary-foreground hover:bg-primary-foreground/10 font-medium px-6 py-2.5">
                <Link to="/TechnicalSolutionsPage">
                  Soluções Técnicas
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Hero Visual & Specs Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.15
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl border border-primary-foreground/15 bg-card/10 backdrop-blur-md p-3 sm:p-4 shadow-2xl">
              <div className="relative overflow-hidden rounded-xl bg-muted aspect-4/3 sm:aspect-16/11">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_lubrication_system.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_lubrication_system.png-wvc-sizes" alt="Sistema industrial de pulverização para lubrificação de precisão Eurocontrol" loading="lazy" decoding="async" className="size-full object-cover" src="https://eurocontrol.pt/wp-content/uploads/2026/07/Captura-de-ecra-2026-07-21-as-18.45.32.png" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
                
                <div className="absolute bottom-4 left-4 right-4 p-3 rounded-lg bg-primary/90 backdrop-blur-sm border border-primary-foreground/10 text-xs sm:text-sm">
                  <span className="font-semibold text-primary-foreground block">Tecnologia AutoJet®</span>
                  <span className="text-primary-foreground/75 block mt-0.5">
                    Controlo de ciclo preciso para estampagem, moldagem e transportadores
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Feature Highlights Grid */}
        <div className="mt-16 pt-12 border-t border-primary-foreground/15 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <div key={feature.title} data-index={i} className="flex items-start gap-4 p-5 rounded-xl bg-primary-foreground/5 border border-primary-foreground/10">
                <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground shrink-0 shadow-xs">
                  <Icon className="size-5" />
                </div>
                <div>
                  <h3 className="font-default text-base font-semibold text-primary-foreground">
                    {feature.title}
                  </h3>
                  <p className="mt-1 text-sm text-primary-foreground/75 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}